/**
 * Configuración de i18next para internacionalización (i18n)
 *
 * i18next permite cambiar el idioma de la aplicación dinámicamente.
 *
 * Estructura de traducciones:
 * - src/locales/es.json → Español (idioma principal)
 * - src/locales/en.json → Inglés
 *
 * Uso en componentes:
 * ```tsx
 * import { useTranslation } from 'react-i18next';
 *
 * function MyComponent() {
 *   const { t } = useTranslation();
 *   return <h1>{t('nav.dashboard')}</h1>; // Muestra "Dashboard" o "Panel de Control"
 * }
 * ```
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Importar archivos de traducción
import es from "../locales/es.json";
import en from "../locales/en.json";
import pt from "../locales/pt.json";

// Definir los recursos de traducción
// Cada idioma tiene su propio objeto con todas las traducciones
const resources = {
  es: {
    translation: es, // 'translation' es el namespace por defecto
  },
  en: {
    translation: en,
  },
  pt: {
    translation: pt,
  },
};

// Inicializar i18next
i18n
  // Usar el detector de idioma del navegador
  // Detecta automáticamente el idioma preferido del usuario
  .use(LanguageDetector)
  // Conectar con React
  .use(initReactI18next)
  // Configuración
  .init({
    resources, // Nuestros archivos de traducción

    // Normalizar idiomas: "en-US" → "en", "pt-BR" → "pt"
    load: "languageOnly",

    // Idioma por defecto si no se detecta ninguno
    fallbackLng: "es",

    // Opciones del detector de idioma
    detection: {
      // Orden de prioridad para detectar el idioma:
      // 1. localStorage - si el usuario ya eligió un idioma antes
      // 2. navigator - idioma del navegador
      order: ["localStorage", "navigator"],

      // Guardar la preferencia del usuario en localStorage
      caches: ["localStorage"],

      // Nombre de la clave en localStorage
      lookupLocalStorage: "lims-language",
    },

    // Interpolación: cómo se reemplazan las variables en las traducciones
    // Ejemplo: "Hola {{name}}" → "Hola Juan"
    interpolation: {
      escapeValue: false, // React ya escapa los valores por seguridad
    },

    // No mostrar warnings en consola en producción
    debug: import.meta.env.DEV,
  });

export default i18n;
